import { useEffect, useReducer } from 'react'

import { GENERATION_PHASE } from 'Pods/ThreeDStudio/Store/constants'

import phaseAnchorMs from './phaseAnchorMs'

export const COUNTDOWN_TICK_MS = 1000
const SECONDS_PER_MINUTE = 60

// The stages measured against the estimate. Before them it is shown as a static figure: a run
// waits in the queue, and then in analyzing while it waits for a provider slot, so a countdown
// started there would empty before the provider had begun.
const COUNTDOWN_PHASES = [GENERATION_PHASE.GENERATING, GENERATION_PHASE.REFINING]

// The backend sends the estimate, not a remainder, so the client subtracts the time spent in the
// phase. Clamped at both ends: the anchor is a server timestamp read against the viewer's clock,
// and a skewed clock must degrade to "the whole estimate" or "almost done", never to a negative
// or an inflated number.
const remainingFor = (run) => {
  const elapsed = Math.max((Date.now() - phaseAnchorMs(run)) / 1000, 0)
  return Math.min(Math.max(run.estimatedSeconds - elapsed, 0), run.estimatedSeconds)
}

/**
 * The time-left copy under the phase title: the run's estimate, counting down once the provider is
 * working against it. Ticks locally while counting down. Returns null when the run carries no
 * estimate, so the caller renders nothing rather than a placeholder number.
 * @param {string} phase - The displayed GENERATION_PHASE value
 * @param {object|undefined} run - The watched activeRuns entry
 * @returns {{ key: string, minutes?: number }|null}
 */
export default function useTimeLeft(phase, run) {
  const [, tick] = useReducer((count) => count + 1, 0)
  const hasEstimate = run?.estimatedSeconds != null
  const isCountingDown = COUNTDOWN_PHASES.includes(phase) && hasEstimate

  useEffect(() => {
    if (!isCountingDown) {
      return undefined
    }

    const intervalId = setInterval(tick, COUNTDOWN_TICK_MS)
    return () => clearInterval(intervalId)
  }, [isCountingDown])

  // Finalizing is upload and screenshot work past the provider's part, and re-anchors the phase
  // timestamp, so there is no estimate left to measure.
  if (phase === GENERATION_PHASE.FINALIZING) {
    return { key: 'lessThanMinute' }
  }
  if (!hasEstimate) {
    return null
  }

  const seconds = isCountingDown ? remainingFor(run) : run.estimatedSeconds
  if (seconds <= SECONDS_PER_MINUTE) {
    return { key: 'lessThanMinute' }
  }

  return { key: 'minutes', minutes: Math.ceil(seconds / SECONDS_PER_MINUTE) }
}
